import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function Icon({ d, className = 'h-6 w-6' }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

function Slide({ image, alt, active }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className="relative flex h-full w-full flex-none snap-center snap-always items-center justify-center">
      {!loaded && <div className="loading loading-spinner loading-md absolute text-white/60" aria-hidden="true" />}
      <img
        src={image.url}
        srcSet={image.srcSet || undefined}
        sizes="100vw"
        alt={image.title || alt}
        decoding="async"
        fetchpriority={active ? 'high' : 'low'}
        onLoad={() => setLoaded(true)}
        draggable={false}
        className={`max-h-full max-w-full select-none object-contain transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
      />
    </div>
  );
}

/**
 * Full-screen photo viewer. Photos sit side by side in a scroll-snap strip, so
 * swiping uses the browser's native momentum. Only the current photo and its
 * neighbours are loaded. Tap to hide the controls; arrow keys and Esc work too.
 */
function PhotoViewer({ images, initialIndex = 0, alt, onIndexChange, onClose }) {
  const stripRef = useRef(null);
  const closeRef = useRef(null);
  const [index, setIndex] = useState(initialIndex);
  const [controls, setControls] = useState(true);
  const indexRef = useRef(initialIndex);

  // Open on the chosen photo without animating past the others, and keep it in
  // place when the phone rotates.
  useLayoutEffect(() => {
    const strip = stripRef.current;
    const align = () => {
      strip.scrollLeft = indexRef.current * strip.clientWidth;
    };
    align();
    window.addEventListener('resize', align);
    return () => window.removeEventListener('resize', align);
  }, []);

  useEffect(() => {
    const { overflow } = document.documentElement.style;
    document.documentElement.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.documentElement.style.overflow = overflow;
    };
  }, []);

  // Report the photo once the strip settles, not on every scroll frame.
  const settleTimer = useRef(null);
  const handleScroll = () => {
    const strip = stripRef.current;
    const next = Math.round(strip.scrollLeft / strip.clientWidth);
    if (next !== indexRef.current && next >= 0 && next < images.length) {
      indexRef.current = next;
      setIndex(next);
    }
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => onIndexChange?.(indexRef.current), 150);
  };
  useEffect(() => () => clearTimeout(settleTimer.current), []);

  const go = useCallback(
    (step) => {
      const strip = stripRef.current;
      const target = Math.min(Math.max(indexRef.current + step, 0), images.length - 1);
      strip.scrollTo({ left: target * strip.clientWidth, behavior: 'smooth' });
    },
    [images.length]
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [go, onClose]);

  const image = images[index];
  const bar = `absolute inset-x-0 z-10 flex items-center text-white transition-opacity duration-200 ${controls ? 'opacity-100' : 'pointer-events-none opacity-0'}`;
  const arrow = 'btn btn-circle btn-ghost absolute top-1/2 z-10 hidden -translate-y-1/2 text-white hover:bg-white/10 md:flex';

  // Rendered on <body> so no transformed ancestor (the page fade-in) can offset it.
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black" role="dialog" aria-modal="true" aria-label={alt}>
      <div
        ref={stripRef}
        onScroll={handleScroll}
        onClick={() => setControls((c) => !c)}
        className="no-scrollbar flex h-full snap-x snap-mandatory overflow-x-auto overscroll-contain"
      >
        {images.map((img, i) =>
          Math.abs(i - index) <= 1 ? (
            <Slide key={img.id} image={img} alt={alt} active={i === index} />
          ) : (
            <div key={img.id} className="h-full w-full flex-none snap-center" />
          )
        )}
      </div>

      <div className={`${bar} top-0 justify-between bg-gradient-to-b from-black/60 to-transparent px-4 pb-8 pt-[max(0.75rem,env(safe-area-inset-top))]`}>
        <span className="text-sm tabular-nums" aria-live="polite">
          {index + 1} / {images.length}
        </span>
        <button ref={closeRef} onClick={onClose} className="btn btn-circle btn-ghost text-white hover:bg-white/10" aria-label="Close">
          <Icon d="M6 18L18 6M6 6l12 12" />
        </button>
      </div>

      {image.title && (
        <div className={`${bar} bottom-0 justify-center bg-gradient-to-t from-black/60 to-transparent px-4 pt-8 pb-[max(1rem,env(safe-area-inset-bottom))] text-center text-sm`}>
          {image.title}
        </div>
      )}

      {index > 0 && (
        <button onClick={() => go(-1)} className={`${arrow} left-4`} aria-label="Previous photo">
          <Icon d="M15 19l-7-7 7-7" className="h-8 w-8" />
        </button>
      )}
      {index < images.length - 1 && (
        <button onClick={() => go(1)} className={`${arrow} right-4`} aria-label="Next photo">
          <Icon d="M9 5l7 7-7 7" className="h-8 w-8" />
        </button>
      )}
    </div>,
    document.body
  );
}

export default PhotoViewer;
